import AlertInline from '@storeblocks/inline-alert';
import { LoadingIndicatorSpinner } from '@storeblocks/spinner-loading-indicator';
import { type FC, useState } from 'react';
import styled from 'styled-components';

import { useUsersQuery } from '@/api/users/usersApi';
import { useCurrentUserInfo } from '@/auth/hooks';
import { Show } from '@/components/Show';
import { UserType } from '@/config/types';
import { useTexts } from '@/hooks/useTexts';
import { padding } from '@/styles/variables';

import { AddAllUsersModal } from './AddAndEditUserModal/AddAllUsers';
import { IndividualUsersTable } from './IndividualUsersTable';
import { Toolbar } from './Toolbar';
import { useUserSearch } from './useUserSearch';

export const IndividualUserAdmin: FC = () => {
  const texts = useTexts();

  const [searchValue, setSearchValue] = useState<string | undefined>();

  const { userType } = useCurrentUserInfo();

  const usersQuery = useUsersQuery(undefined, {
    skip: userType !== UserType.Internal,
  });

  const { activeUsers, inactiveUsers } = useUserSearch(
    searchValue,
    usersQuery.data,
  );

  if (usersQuery.isError) {
    return (
      <Wrapper>
        <AlertInline variant="error" title={texts.admin.user.loadError} />
      </Wrapper>
    );
  }

  if (usersQuery.isLoading) {
    return (
      <LoadingWrapper>
        <LoadingIndicatorSpinner />
      </LoadingWrapper>
    );
  }

  return (
    <Wrapper>
      <Toolbar
        onSearchParamsChange={(params) => setSearchValue(params.query)}
        modal={AddAllUsersModal}
      />

      <Show when={inactiveUsers.length > 0}>
        <Wrapper>
          <Label>{texts.admin.user.inactiveUsers}</Label>
          <IndividualUsersTable individualUsers={inactiveUsers} />
        </Wrapper>
      </Show>

      <Show when={activeUsers.length > 0}>
        <Wrapper>
          {/* Only show label if the table above is visible. */}
          <Show when={inactiveUsers.length > 0}>
            <Label>{texts.admin.user.users}</Label>
          </Show>

          <IndividualUsersTable individualUsers={activeUsers} />
        </Wrapper>
      </Show>

      <Show when={activeUsers.length === 0 && inactiveUsers.length === 0}>
        <AlertInline title={texts.admin.users.none} />
      </Show>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: 32px;
`;

const Label = styled.div`
  margin-top: 32px;
  margin-bottom: ${padding.medium}px;
  font-weight: bold;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
`;
